/**
 * Eye Dome Lighting (EDL) Post-Processing Pass for Three.js
 *
 * Adapted from the EDL shader code by Christian Boucheny used in
 * CloudCompare and Potree. EDL is a non-photorealistic shading technique
 * that enhances depth perception by darkening silhouettes and depth
 * discontinuities.
 *
 * Usage:
 *   const edlPass = new EDLPass(scene, camera, width, height);
 *   composer.addPass(edlPass);
 *   edlPass.enabled = true;  // toggle on/off
 *   edlPass.edlStrength = 1.0;
 *   edlPass.edlRadius = 1.0;
 */
import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
/** 8-direction neighbor kernel for depth sampling */
const NEIGHBOR_OFFSETS = [
    [0, 1], // N
    [1, 1], // NE
    [1, 0], // E
    [1, -1], // SE
    [0, -1], // S
    [-1, -1], // SW
    [-1, 0], // W
    [-1, 1], // NW
];
const EDLVertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;
const EDLFragmentShader = /* glsl */ `
// Eye Dome Lighting fragment shader
// Adapted from Christian Boucheny's EDL implementation (CloudCompare/Potree)

uniform sampler2D tDiffuse;     // Color buffer from scene render
uniform sampler2D tDepth;       // Depth buffer from scene render
uniform sampler2D tEdlDepth;    // Visible depth of Auto-mode eligible point clouds
uniform bool selective;
uniform float screenWidth;
uniform float screenHeight;
uniform float edlStrength;
uniform float radius;
uniform float cameraNear;
uniform float cameraFar;
uniform float responseScale;
uniform float emptyPixelBoost;
uniform float secondRingWeight;

// 8-direction neighbor offsets
uniform vec2 neighbours[8];

varying vec2 vUv;

/**
 * Linearize depth from the depth buffer.
 * The hardware depth buffer uses a hyperbolic distribution;
 * we convert to linear [0,1] range for meaningful comparisons.
 */
float getLinearDepth(vec2 coord) {
  float fragDepth = texture2D(tDepth, coord).r;
  // If depth is essentially at the far plane, treat as "no geometry"
  if (fragDepth >= 1.0) return 0.0;
  // Convert hyperbolic depth to linear
  float viewZ = (cameraNear * cameraFar) / (cameraFar - fragDepth * (cameraFar - cameraNear));
  return viewZ;
}

/**
 * Compute the EDL response for a pixel.
 * Compares the log2-depth of the current pixel against its neighbors.
 * Large depth differences indicate edges/silhouettes and produce a high response.
 */
float edlResponse(float depth) {
  vec2 texelSize = radius / vec2(screenWidth, screenHeight);

  float sum = 0.0;
  float secondRingSum = 0.0;

  for (int i = 0; i < 8; i++) {
    vec2 baseOffset = texelSize * neighbours[i];
    vec2 neighbourCoord = vUv + baseOffset;
    float neighbourDepth = getLinearDepth(neighbourCoord);

    if (neighbourDepth > 0.0) {
      if (depth <= 0.0) {
        // Current pixel has no geometry but neighbor does — strong edge
        sum += emptyPixelBoost;
      } else {
        // Compare log2 depths for scale-invariant edge detection
        sum += max(0.0, log2(depth) - log2(neighbourDepth));
      }
    }

    if (secondRingWeight > 0.0) {
      vec2 secondRingCoord = vUv + baseOffset * 2.0;
      float secondRingDepth = getLinearDepth(secondRingCoord);
      if (secondRingDepth > 0.0) {
        if (depth <= 0.0) {
          secondRingSum += emptyPixelBoost;
        } else {
          secondRingSum += max(0.0, log2(depth) - log2(secondRingDepth));
        }
      }
    }
  }

  float base = sum / 8.0;
  if (secondRingWeight > 0.0) {
    float ring2 = secondRingSum / 8.0;
    // Multi-scale response with normalization to avoid globally darker output.
    return (base + ring2 * secondRingWeight) / (1.0 + secondRingWeight);
  }
  return base;
}

void main() {
  vec4 color = texture2D(tDiffuse, vUv);
  float rawDepth = texture2D(tDepth, vUv).r;
  float depth = getLinearDepth(vUv);
  float shade = 1.0;

  if (depth > 0.0) {
    bool shadePixel = true;
    if (selective) {
      float edlDepth = texture2D(tEdlDepth, vUv).r;
      // Both depth textures render the same scene and camera. The small epsilon
      // tolerates texture precision without allowing an eligible cloud hidden
      // behind a coloured cloud to darken the foreground pixel.
      shadePixel = edlDepth < 1.0 && abs(edlDepth - rawDepth) <= 0.000001;
    }
    if (shadePixel) {
      float response = edlResponse(depth);
      // Exponential falloff produces natural-looking shadows.
      shade = exp(-response * responseScale * edlStrength);
    }
  }

  gl_FragColor = vec4(color.rgb * shade, color.a);
  // The scene render target is linear and deliberately bypasses the renderer's
  // output transform. Apply the same tone mapping and display colour conversion
  // here that a direct renderer.render() performs. Without these chunks, even
  // pixels excluded by Auto changed colour merely by passing through EDL.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;
/**
 * Eye Dome Lighting post-processing pass.
 *
 * Renders the scene to an off-screen target with depth, then applies
 * the EDL shader as a full-screen effect that darkens edges where
 * depth changes sharply.
 */
export class EDLPass extends Pass {
    constructor(scene, camera, width, height, options = {}) {
        super();
        this.selective = false;
        this.eligibleObjects = new Set();
        this.scene = scene;
        this.camera = camera;
        this.edlStrength = options.strength ?? 1.0;
        this.edlRadius = options.radius ?? 1.4;
        this.secondRingWeight = options.secondRingWeight ?? 0.0;
        this._width = width;
        this._height = height;
        // Create render target with attached depth texture
        this.renderTarget = new THREE.WebGLRenderTarget(width, height, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            type: THREE.HalfFloatType,
        });
        // Attach a depth texture so we can read depth in the EDL shader
        this.renderTarget.depthTexture = new THREE.DepthTexture(width, height);
        this.renderTarget.depthTexture.type = THREE.FloatType;
        // Auto mode needs to know whether the frontmost pixel belongs to a
        // uniformly coloured point cloud. Rendering only those clouds into a
        // second depth texture preserves the full scene depth for edge detection
        // while giving the composite shader a precise per-pixel eligibility mask.
        this.edlDepthTarget = new THREE.WebGLRenderTarget(width, height, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            type: THREE.UnsignedByteType,
        });
        this.edlDepthTarget.depthTexture = new THREE.DepthTexture(width, height);
        this.edlDepthTarget.depthTexture.type = THREE.FloatType;
        // Build uniform neighbor array
        const neighbourUniforms = NEIGHBOR_OFFSETS.map(([x, y]) => new THREE.Vector2(x, y));
        // Create the EDL shader material
        this.edlMaterial = new THREE.ShaderMaterial({
            uniforms: {
                tDiffuse: { value: null },
                tDepth: { value: null },
                tEdlDepth: { value: null },
                selective: { value: false },
                screenWidth: { value: width },
                screenHeight: { value: height },
                edlStrength: { value: this.edlStrength },
                radius: { value: this.edlRadius },
                responseScale: { value: 300.0 },
                emptyPixelBoost: { value: 100.0 },
                secondRingWeight: { value: this.secondRingWeight },
                cameraNear: { value: 0.001 },
                cameraFar: { value: 10000000 },
                neighbours: { value: neighbourUniforms },
            },
            vertexShader: EDLVertexShader,
            fragmentShader: EDLFragmentShader,
            depthWrite: false,
            depthTest: false,
        });
        this.fsQuad = new FullScreenQuad(this.edlMaterial);
    }
    /** Configure whether this frame shades all geometry or only these point clouds. */
    setEligibleObjects(objects) {
        this.selective = objects !== null;
        this.eligibleObjects = new Set(objects ?? []);
    }
    /** Exposed for diagnostics and browser regressions without exposing the set itself. */
    get eligibleObjectCount() {
        return this.eligibleObjects.size;
    }
    get isSelective() {
        return this.selective;
    }
    /**
     * Render the EDL effect.
     * 1. Render the scene to our render target (capturing color + depth)
     * 2. Apply the EDL shader as a full-screen post-process
     */
    render(renderer, writeBuffer, _readBuffer
    /*, deltaTime?: number, maskActive?: boolean */
    ) {
        // Step 1: Render scene to our render target (captures color + depth)
        renderer.setRenderTarget(this.renderTarget);
        renderer.render(this.scene, this.camera);
        if (this.selective) {
            const visibility = new Map();
            this.scene.traverse(object => {
                const renderable = object instanceof THREE.Points ||
                    object instanceof THREE.Mesh ||
                    object instanceof THREE.Line ||
                    object instanceof THREE.Sprite;
                if (!renderable) {
                    return;
                }
                visibility.set(object, object.visible);
                object.visible = object.visible && this.eligibleObjects.has(object);
            });
            try {
                renderer.setRenderTarget(this.edlDepthTarget);
                renderer.render(this.scene, this.camera);
            }
            finally {
                visibility.forEach((visible, object) => (object.visible = visible));
            }
        }
        // Step 2: Update EDL uniforms
        const cam = this.camera;
        this.edlMaterial.uniforms.tDiffuse.value = this.renderTarget.texture;
        this.edlMaterial.uniforms.tDepth.value = this.renderTarget.depthTexture;
        this.edlMaterial.uniforms.tEdlDepth.value = this.edlDepthTarget.depthTexture;
        this.edlMaterial.uniforms.selective.value = this.selective;
        this.edlMaterial.uniforms.edlStrength.value = this.edlStrength;
        this.edlMaterial.uniforms.radius.value = this.edlRadius;
        this.edlMaterial.uniforms.responseScale.value = 300.0;
        this.edlMaterial.uniforms.emptyPixelBoost.value = 100.0;
        this.edlMaterial.uniforms.secondRingWeight.value = this.secondRingWeight;
        this.edlMaterial.uniforms.screenWidth.value = this._width;
        this.edlMaterial.uniforms.screenHeight.value = this._height;
        if (cam.near !== undefined) {
            this.edlMaterial.uniforms.cameraNear.value = cam.near;
        }
        if (cam.far !== undefined) {
            this.edlMaterial.uniforms.cameraFar.value = cam.far;
        }
        // Step 3: Render the full-screen quad with EDL shader
        if (this.renderToScreen) {
            renderer.setRenderTarget(null);
        }
        else {
            renderer.setRenderTarget(writeBuffer);
        }
        this.fsQuad.render(renderer);
    }
    /**
     * Update the render target size (call on window resize).
     */
    setSize(width, height) {
        this._width = width;
        this._height = height;
        this.renderTarget.setSize(width, height);
        this.edlDepthTarget.setSize(width, height);
    }
    /**
     * Clean up GPU resources.
     */
    dispose() {
        this.renderTarget.dispose();
        this.edlDepthTarget.dispose();
        this.edlMaterial.dispose();
        this.fsQuad.dispose();
    }
}
